/**
 * Demo Wallet API
 *
 * 모의거래 지갑 관련 API 호출 함수
 *
 * ⚠️ IMPORTANT: 신규 API (port 4333) 호출을 위해 apiClient 사용
 *
 * @see coin-server/apps/api/src/demo/handlers/demo-wallet-deposit.handler.ts
 */

import apiClient from '@utils/apiClient';

export interface DemoWalletDepositRequest {
  amount: number;
  walletSymbol?: string;
  walletType?: number;
}

export interface DemoWalletInfo {
  id: number;
  userId: number;
  walletSymbol: string;
  walletType: number;
  balance: string;
  lockbalance: string;
  eventBalance: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DemoWalletDepositResponse {
  success: boolean;
  data: DemoWalletInfo;
}

/**
 * Demo 지갑 입금
 *
 * POST /v1/demo/wallets/deposit
 *
 * @param amount - 입금 금액 (0.01 ~ 1,000,000 USDT)
 * @param walletSymbol - 지갑 심볼 (기본: 'USDT')
 * @param walletType - 지갑 타입 (0: Spot, 1: Futures, 7: Event, 기본: 1)
 * @returns 업데이트된 지갑 정보
 */
export const depositToDemoWallet = async (
  amount: number,
  walletSymbol: string = 'USDT',
  walletType: number = 1
): Promise<DemoWalletDepositResponse> => {
  // apiClient는 baseURL이 NEW_API_URL (port 4333)로 설정되어 있음
  // 요청 URL: {NEW_API_URL}/demo/wallets/deposit
  const response = await apiClient.post<DemoWalletDepositResponse>(
    '/demo/wallets/deposit',
    {
      amount,
      walletSymbol,
      walletType,
    }
  );

  return response.data;
};

/**
 * Demo 지갑 API 헬퍼
 */
export const demoWalletApi = {
  /**
   * Demo 지갑 입금
   * @param amount - 입금 금액
   */
  async deposit(amount: number): Promise<DemoWalletDepositResponse> {
    return depositToDemoWallet(amount);
  },

  /**
   * Demo 지갑 Spot 입금
   * @param amount - 입금 금액
   */
  async depositToSpot(amount: number): Promise<DemoWalletDepositResponse> {
    return depositToDemoWallet(amount, 'USDT', 0);
  },

  /**
   * Demo 지갑 Futures 입금
   * @param amount - 입금 금액
   */
  async depositToFutures(amount: number): Promise<DemoWalletDepositResponse> {
    return depositToDemoWallet(amount, 'USDT', 1);
  },
};
